// src/components/admin/SyncRows.tsx
// -----------------------------------------------------------------------------
// SyncRows — compact one-line summary rows for upstream-data syncs.
// Mounted at the top of the Superadmin → Overview tab (Joan, 2026-05-27).
//
// Currently covers:
//   1. XXMM Legacy Sync (per-config row, default action = incremental sync)
//   2. Clinical Trial Registry
//
// Both rows share the same visual recipe: title + caption line with last-sync
// stats + a "Sync Now" button on the right. A subtle status chip overlays
// the row when a sync is running; the button morphs into "Cancel" only for
// XXMM (Trial sync isn't cancellable).
// -----------------------------------------------------------------------------

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorageIcon from "@mui/icons-material/Storage";
import SyncIcon from "@mui/icons-material/Sync";
import { useSnackbar } from "notistack";

import { useTranslation } from "../../i18n";
import { createData, fetchData } from "../../api/apiHelpers";
import { glass } from "../../theme/glassTokens";

// Best-effort live-progress payload written by the backend sync engine + image task.
// Data phases carry a label only (current/total null); the image download phase fills
// current/total. `done` flips true when the (long) image phase finishes or fails.
interface SyncProgress {
  phase: string;
  current: number | null;
  total: number | null;
  done?: boolean;
}

interface XxmmLog {
  status: string;
  mode: string;
  patients_synced: number;
  patients_skipped: number;
  events_synced: number;
  duration_seconds: number | null;
  dry_run: boolean;
  errors: string[];
  progress?: SyncProgress | null;
}

interface XxmmConfig {
  id: number;
  environment_name: string;
  institution_filter: number[];
  is_active: boolean;
  last_sync_at: string | null;
  last_log: XxmmLog | null;
}

// A sync is "in progress" for the UI while the log status is `running` (the fast data
// phases) OR while the image-download phase is still active. The image phase runs as a
// follow-up task AFTER the data-sync log is finalized to success, so status alone is not
// enough to keep the bar visible — the progress payload drives it. `done` (or reaching
// total) ends it.
const isImagePhaseActive = (p?: SyncProgress | null): boolean =>
  !!p &&
  p.phase === "Downloading images" &&
  !p.done &&
  (p.total == null || p.current == null || p.current < p.total);

const isLogActive = (log?: XxmmLog | null): boolean =>
  !!log && (log.status === "running" || isImagePhaseActive(log.progress));

interface TrialSyncStatus {
  total_trials: number;
  recruiting: number;
  last_synced: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Shared row shell
// ────────────────────────────────────────────────────────────────────────────

interface RowShellProps {
  title: string;
  caption: React.ReactNode;
  primaryAction: React.ReactNode;
  trailingAction?: React.ReactNode;
}

const RowShell: React.FC<RowShellProps> = ({
  title,
  caption,
  primaryAction,
  trailingAction,
}) => {
  const textPrimary =
    glass.textPrimary;
  const textSecondary =
    glass.textSecondary;
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: 2,
        background: glass.panel,
        border: `1px solid ${glass.panelBorder}`,
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ color: textPrimary }}
        >
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: textSecondary }}>
          {caption}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
        {primaryAction}
        {trailingAction}
      </Box>
    </Box>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Live progress bar (polls the status endpoint while a sync is in progress)
// ────────────────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5000;

interface SyncProgressBarProps {
  configId: number;
  initialLog: XxmmLog | null;
  // Called once when a previously-active sync becomes idle, so the parent can
  // refresh its (now final) stats/last-sync line.
  onSettled: () => void;
}

const SyncProgressBar: React.FC<SyncProgressBarProps> = ({
  configId,
  initialLog,
  onSettled,
}) => {
  const { t } = useTranslation();
  const textSecondary =
    glass.textSecondary;

  const [log, setLog] = useState<XxmmLog | null>(initialLog);
  useEffect(() => setLog(initialLog), [initialLog]);

  const active = isLogActive(log);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await fetchData<XxmmLog>(`/xxmm-sync/status/${configId}/`);
        if (cancelled) return;
        setLog(next);
        if (!isLogActive(next)) onSettled();
      } catch {
        /* transient error — keep polling */
      }
    };
    const handle = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [active, configId, onSettled]);

  if (!active || !log?.progress) return null;

  const { phase, current, total } = log.progress;
  const phaseLabels: Record<string, string> = {
    "Reading source": t("admin.xxmm_phase_reading", "Reading source"),
    Preparing: t("admin.xxmm_phase_preparing", "Preparing"),
    "Writing patients": t("admin.xxmm_phase_writing_patients", "Writing patients"),
    "Writing clinical tables": t(
      "admin.xxmm_phase_writing_clinical",
      "Writing clinical tables",
    ),
    "Downloading images": t("admin.xxmm_phase_images", "Downloading images"),
  };
  const phaseLabel = phaseLabels[phase] || phase;

  const determinate =
    typeof current === "number" && typeof total === "number" && total > 0;
  const value = determinate
    ? Math.min(100, Math.round((current! / total!) * 100))
    : 0;
  const countLabel = determinate
    ? `${current!.toLocaleString()} / ${total!.toLocaleString()}`
    : "";

  return (
    <Box sx={{ mt: 1, px: { xs: 0.5, sm: 1 } }}>
      <Typography
        variant="caption"
        sx={{ color: textSecondary, display: "block", mb: 0.5 }}
      >
        {countLabel ? `${phaseLabel} ${countLabel}` : phaseLabel}
      </Typography>
      <LinearProgress
        variant={determinate ? "determinate" : "indeterminate"}
        value={value}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: alpha(glass.accent, 0.15),
          "& .MuiLinearProgress-bar": { borderRadius: 3 },
        }}
      />
    </Box>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// XXMM row(s)
// ────────────────────────────────────────────────────────────────────────────

const XxmmRows: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const textSecondary =
    glass.textSecondary;

  const [configs, setConfigs] = useState<XxmmConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<Record<number, boolean>>({});
  const [cancelling, setCancelling] = useState<Record<number, boolean>>({});
  const [menuAnchor, setMenuAnchor] = useState<{
    el: HTMLElement;
    cfgId: number;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchData<XxmmConfig[]>("/xxmm-sync/configs/");
      setConfigs(data || []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = async (
    cfgId: number,
    mode: "incremental" | "full",
    dryRun = false,
    // When false, the data sync runs WITHOUT the follow-up disease-image download
    // (which can take hours). Default true keeps the historical full behaviour.
    syncImages = true,
  ) => {
    setSyncing((s) => ({ ...s, [cfgId]: true }));
    try {
      await createData("/xxmm-sync/trigger/", {
        config_id: cfgId,
        mode,
        dry_run: dryRun,
        sync_images: syncImages,
      });
      enqueueSnackbar(
        t(
          "admin.xxmm_sync_started",
          "{mode} sync started. Refresh in a few minutes.",
        ).replace("{mode}", mode === "full" ? "Full" : "Incremental"),
        { variant: "info" },
      );
      setTimeout(load, 4000);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || (err as Error)?.message;
      enqueueSnackbar(msg || "Failed to trigger XXMM sync", { variant: "error" });
    } finally {
      setSyncing((s) => ({ ...s, [cfgId]: false }));
    }
  };

  const handleCancel = async (cfgId: number) => {
    setCancelling((c) => ({ ...c, [cfgId]: true }));
    try {
      await createData(`/xxmm-sync/cancel/${cfgId}/`, {});
      enqueueSnackbar(t("admin.xxmm_cancel_requested", "Sync cancellation requested."), {
        variant: "warning",
      });
      setTimeout(load, 3000);
    } catch {
      enqueueSnackbar("Failed to cancel sync", { variant: "error" });
    } finally {
      setCancelling((c) => ({ ...c, [cfgId]: false }));
    }
  };

  if (loading) {
    return <Skeleton variant="rounded" height={72} sx={{ mb: 1.5 }} />;
  }

  if (configs.length === 0) {
    return (
      <RowShell
        title={t("admin.xxmm_title", "XXMM Legacy Sync")}
        caption={
          <>
            <StorageIcon
              sx={{ fontSize: 12, mr: 0.5, verticalAlign: "middle" }}
            />
            {t(
              "admin.xxmm_no_configs",
              "No sync configurations. Create one in Django Admin.",
            )}
          </>
        }
        primaryAction={
          <Tooltip title={t("admin.refresh", "Refresh")}>
            <IconButton size="small" onClick={load}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />
    );
  }

  return (
    <>
      {configs.map((cfg) => {
        const log = cfg.last_log;
        const isRunning = log?.status === "running";
        const isSyncing = syncing[cfg.id];
        const isCancelling = cancelling[cfg.id];

        const stats = log
          ? `${log.patients_synced.toLocaleString()} patients · ${log.events_synced.toLocaleString()} events` +
            (log.patients_skipped > 0 ? ` · ${log.patients_skipped} skipped` : "") +
            (log.duration_seconds != null
              ? ` · ${Math.round(log.duration_seconds / 60)} min`
              : "")
          : t("admin.xxmm_never_synced", "Never synced");
        const lastSync = cfg.last_sync_at
          ? ` · ${t("admin.last_sync", "Last sync")}: ${new Date(cfg.last_sync_at).toLocaleString()}`
          : "";
        const scope =
          cfg.institution_filter && cfg.institution_filter.length > 0
            ? ` · ${cfg.institution_filter.length} ${t("admin.xxmm_institutions", "institutions")}`
            : ` · ${t("admin.xxmm_all_institutions", "all institutions")}`;

        return (
          <Box key={cfg.id} sx={{ mb: 1.5 }}>
            <RowShell
              title={cfg.environment_name}
              caption={
                <>
                  {isRunning && (
                    <Chip
                      label={t("admin.running", "running")}
                      color="info"
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        mr: 0.75,
                      }}
                    />
                  )}
                  {!cfg.is_active && (
                    <Chip
                      label="inactive"
                      size="small"
                      sx={{ height: 16, fontSize: "0.6rem", mr: 0.75 }}
                    />
                  )}
                  <span style={{ color: textSecondary }}>
                    {stats}
                    {lastSync}
                    {scope}
                  </span>
                </>
              }
              primaryAction={
                isRunning ? (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={
                      isCancelling ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <CancelIcon />
                      )
                    }
                    disabled={isCancelling}
                    onClick={() => handleCancel(cfg.id)}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    {t("admin.cancel", "Cancel")}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      isSyncing ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <SyncIcon />
                      )
                    }
                    disabled={isSyncing || !cfg.is_active}
                    onClick={() => handleSync(cfg.id, "incremental")}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    {isSyncing
                      ? t("admin.syncing", "Syncing…")
                      : t("admin.sync_now", "Sync Now")}
                  </Button>
                )
              }
              trailingAction={
                <>
                  <Tooltip title={t("admin.more_actions", "More actions")}>
                    <IconButton
                      size="small"
                      onClick={(e) =>
                        setMenuAnchor({ el: e.currentTarget, cfgId: cfg.id })
                      }
                      disabled={isSyncing || isRunning}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              }
            />
            <SyncProgressBar
              configId={cfg.id}
              initialLog={log}
              onSettled={load}
            />
          </Box>
        );
      })}

      <Menu
        anchorEl={menuAnchor?.el ?? null}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (menuAnchor) handleSync(menuAnchor.cfgId, "full");
            setMenuAnchor(null);
          }}
        >
          <SyncIcon fontSize="small" sx={{ mr: 1 }} />
          {t("admin.xxmm_full_sync", "Full sync")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchor)
              handleSync(menuAnchor.cfgId, "full", false, false);
            setMenuAnchor(null);
          }}
        >
          <StorageIcon fontSize="small" sx={{ mr: 1 }} />
          {t("admin.xxmm_full_sync_data_only", "Full sync — data only (no images)")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchor) handleSync(menuAnchor.cfgId, "incremental", true);
            setMenuAnchor(null);
          }}
        >
          <SyncIcon fontSize="small" sx={{ mr: 1 }} />
          {t("admin.xxmm_dry_run", "Dry run (incremental)")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            load();
            setMenuAnchor(null);
          }}
        >
          <RefreshIcon fontSize="small" sx={{ mr: 1 }} />
          {t("admin.refresh_status", "Refresh status")}
        </MenuItem>
      </Menu>
    </>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Trial Registry row
// ────────────────────────────────────────────────────────────────────────────

const TrialRegistryRow: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState<TrialSyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchData<TrialSyncStatus>("/trials/sync-status/");
      setStatus(data);
    } catch {
      /* not yet synced */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await createData("/trials/sync-trigger/", {});
      enqueueSnackbar(
        t(
          "trials.sync_started",
          "Clinical trial sync started. This may take a few minutes.",
        ),
        { variant: "info" },
      );
      setTimeout(load, 5000);
    } catch {
      enqueueSnackbar(t("trials.sync_error", "Failed to trigger trial sync"), {
        variant: "error",
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <RowShell
      title={t("trials.registry_title", "Clinical Trial Registry")}
      caption={
        status
          ? `${status.total_trials.toLocaleString()} trials synced (${status.recruiting.toLocaleString()} recruiting)` +
            (status.last_synced
              ? ` · ${t("admin.last_sync", "Last sync")}: ${new Date(status.last_synced).toLocaleString()}`
              : "")
          : t("trials.not_synced", "Not yet synced with ClinicalTrials.gov")
      }
      primaryAction={
        <Button
          variant="outlined"
          size="small"
          startIcon={
            syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />
          }
          disabled={syncing}
          onClick={handleSync}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {syncing
            ? t("trials.syncing", "Syncing…")
            : t("trials.sync_now", "Sync Now")}
        </Button>
      }
    />
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Combined component (default export) — mounts both blocks
// ────────────────────────────────────────────────────────────────────────────

const SyncRows: React.FC = () => {
  const textPrimary =
    glass.textPrimary;
  const { t } = useTranslation();
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle1"
        fontWeight={700}
        sx={{ color: textPrimary, mb: 1.5 }}
      >
        {t("admin.data_syncs", "Data syncs")}
      </Typography>
      <XxmmRows />
      <Box sx={{ mt: 1.5 }}>
        <TrialRegistryRow />
      </Box>
    </Box>
  );
};

export default SyncRows;
